import { useCallback, useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { BellRing, Eye, Send, Save } from "lucide-react";
import { EmailSampleBar } from "./EmailSampleBar";
import { getVzAlertsConfig, updateVzAlertsConfig, previewVzAlerts, runVzAlerts, getVzAlertsLog } from "@/lib/verizonSalesApi";

const EmailList = ({ emails, testId, preview = false }) => (
  <div className="space-y-2" data-testid={testId}>
    {emails.map((e) => (
      <div key={e.id} className="border border-slate-100 rounded-lg p-2.5 text-xs">
        <div className="flex justify-between gap-2 flex-wrap">
          <span className="font-medium text-navy">{e.dealer_name || e.dealer_email} · {e.dealer_email}</span>
          <span className="text-slate-500">{!preview && e.sent_at ? new Date(e.sent_at).toLocaleString() : ""} {e.status && <b className={e.status === "sent" ? "text-emerald-600" : "text-red-600"}>{e.status}</b>}</span>
        </div>
        <div className="text-slate-600 mt-1">{e.subject}</div>
        <div className="text-slate-500 mt-0.5">
          {e.low.map((s) => (s.behind ? `${s.label} (behind: ${s.behind.join(", ")})` : `${s.label} (${(s.pacing * 100).toFixed(1)}%)`)).join(" · ")}
          {e.recovered.length > 0 && <span className="text-emerald-600"> · Recovered: {e.recovered.map((s) => s.label).join(", ")}</span>}
        </div>
      </div>
    ))}
  </div>
);

export const PacingAlertsPanel = () => {
  const [cfg, setCfg] = useState(null);
  const [preview, setPreview] = useState(null);
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadLog = useCallback(() => getVzAlertsLog().then(setLog).catch(() => {}), []);
  useEffect(() => { getVzAlertsConfig().then(setCfg).catch(() => toast.error("Failed to load alert settings")); loadLog(); }, [loadLog]);
  if (!cfg) return null;

  const save = async () => {
    try { setCfg(await updateVzAlertsConfig(cfg)); toast.success("Alert settings saved"); }
    catch { toast.error("Save failed"); }
  };
  const doPreview = async () => {
    setBusy(true);
    try { setPreview(await previewVzAlerts()); } catch { toast.error("Preview failed"); } finally { setBusy(false); }
  };
  const sendNow = async () => {
    if (!window.confirm("Send pacing alert emails to dealers now?")) return;
    setBusy(true);
    try {
      const r = await runVzAlerts();
      toast.success(r.skipped || `Sent ${r.emails.length} email(s)`);
      setPreview(null); loadLog();
    } catch { toast.error("Send failed"); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="vz-alerts-panel">
      <Card className="border-slate-200">
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><BellRing className="w-4 h-4 text-teal" /> Pacing alert settings</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-slate-500">After each sync, dealers get an email when an Open store paces below the threshold on any of the 8 Verizon KPIs (Activations, Upgrades, Edge, Tablets, FWA, TWP+, Top Ups, AutoPay; at most once a week per store). Each store gets a scorecard with Goal, Current, Pacing and % to Goal, behind KPIs in red, plus a “back on track” email when it recovers. A copy goes to the CC address.</p>
          <div className="flex items-center gap-3">
            <Switch checked={cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} data-testid="vz-alerts-enabled-switch" />
            <span className="text-sm font-medium">{cfg.enabled ? "Alerts are ON" : "Alerts are OFF"}</span>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="space-y-1.5"><Label className="text-xs">Threshold (% of quota)</Label>
              <Input type="number" min={5} max={200} value={Math.round(cfg.threshold * 100)} onChange={(e) => setCfg({ ...cfg, threshold: Number(e.target.value) / 100 })} data-testid="vz-alerts-threshold-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Start alerts on day of month</Label>
              <Input type="number" min={1} max={28} value={cfg.start_day} onChange={(e) => setCfg({ ...cfg, start_day: Number(e.target.value) })} data-testid="vz-alerts-startday-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs">CC email</Label>
              <Input value={cfg.cc_email || ""} onChange={(e) => setCfg({ ...cfg, cc_email: e.target.value })} data-testid="vz-alerts-cc-input" /></div>
          </div>
          <div className="flex gap-2 flex-wrap justify-end">
            <Button variant="outline" onClick={doPreview} disabled={busy} data-testid="vz-alerts-preview-btn"><Eye className="w-4 h-4 mr-1" /> Preview who gets emailed</Button>
            <Button variant="outline" onClick={sendNow} disabled={busy} data-testid="vz-alerts-send-btn"><Send className="w-4 h-4 mr-1" /> Send now</Button>
            <Button onClick={save} data-testid="vz-alerts-save-btn"><Save className="w-4 h-4 mr-1" /> Save settings</Button>
          </div>
          <EmailSampleBar kind="pacing" />
          {preview && (
            <div className="border-t border-slate-100 pt-3" data-testid="vz-alerts-preview">
              <div className="text-xs font-semibold text-slate-600 mb-2">{preview.skipped || `${preview.emails.length} dealer email(s) would be sent (preview only)`}</div>
              <EmailList emails={preview.emails} testId="vz-alerts-preview-list" preview />
            </div>
          )}
        </CardContent>
      </Card>
      <Card className="border-slate-200">
        <CardHeader className="pb-2"><CardTitle className="text-base">Sent alerts</CardTitle></CardHeader>
        <CardContent>{log.length ? <EmailList emails={log} testId="vz-alerts-log" /> : <p className="text-xs text-slate-500">No alerts sent yet.</p>}</CardContent>
      </Card>
    </div>
  );
};
